import { omit } from 'lodash';
import { computed, makeAutoObservable, observable, toJS } from 'mobx';
import { Common } from 'src/AppState/Common';
import { ToasterState } from 'src/AppState/ToasterState';

import { TrpcClient } from 'src/AppState/TrpcClient';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { DateTime } from 'src_common/utils/time/time';
import {
    AggregationsType,
    EnabledTransactionType,
    RequestDataType,
    SuccessResponseType,
} from 'src_server/types/paymentSearch/paymentsSearchTypes';
import {
    isStakeNumeric,
    isDateValid,
    isStakeGreaterOrEqual,
    isDateAfter,
    isDateBefore,
} from './PaymentSearch.validators';

export const WITHDRAWAL_SWITCHES = [
    { label: 'Requested', key: 'requested' },
    { label: 'Initiated', key: 'initiated' },
    { label: 'Paid', key: 'paid' },
    { label: 'Failed', key: 'failed' },
    { label: 'Cancelled', key: 'cancelled' },
    { label: 'Rejected', key: 'rejected' },
    { label: 'Error', key: 'internal-error' },
] as const;
export const DEPOSIT_SWITCHES = [
    { label: 'Initiated', key: 'initiated' },
    { label: 'Paid', key: 'paid' },
    { label: 'Failed', key: 'failed' },
    { label: 'Cancelled', key: 'cancelled' },
    { label: 'Error', key: 'internal-error' },
] as const;
export const ADJUSTMENTS_SWITCHES = [
    { label: 'Bank Transfer', key: 'bank-transfer' },
    { label: 'Cash', key: 'cash' },
    { label: 'Cheque', key: 'cheque' },
] as const;
export const MANUAL_SWITCHES = [
    { label: 'Manual - Trading', key: 'trading' },
    { label: 'Manual - Non-Trading', key: 'other' },
] as const;
export const CASINO_SWITCHES = [{ label: 'Casino', key: 'casiono-bonus' }] as const;

export interface PaymentSearchFormType {
    account: string;
    fromDate: string;
    toDate: string;
    fromAmount: string;
    toAmount: string;
    types: RequestDataType['types'] | {};
    includeSummary: boolean;
    saveReport: boolean;
    transactionType: string;
}

export class PaymentSearchState {
    public readonly account: FormInputState<string, string>;
    public readonly types: FormInputState<RequestDataType['types'], RequestDataType['types']>;
    public readonly fromDate: FormInputState<string, string>;
    public readonly toDate: FormInputState<string, string>;
    public readonly fromAmount: FormInputState<string, string>;
    public readonly toAmount: FormInputState<string, string>;
    public readonly includeSummary: FormInputState<boolean, boolean>;
    public readonly saveReport: FormInputState<boolean, boolean>;
    public readonly transactionType: FormInputState<string, string>;
    public readonly form: FormModel<PaymentSearchFormType>;
    @observable public results: SuccessResponseType | undefined = undefined;
    @observable public requestQuery: RequestDataType | undefined = undefined;

    public constructor(
        private readonly common: Common,
        private readonly trpcClient: TrpcClient
    ) {
        this.account = FormInputState.new('');
        this.fromDate = FormInputState.new(DateTime.current().subtractDays(7).startOfDays().toISOString())
            .map(isDateValid(`Enter a valid date.`))
            .map(isDateBefore(() => this.toDate.value, true, `'From' date must be before 'To' date.`));
        this.toDate = FormInputState.new(DateTime.current().endOfDays().toISOString())
            .map(isDateValid(`Enter a valid date.`))
            .map(isDateAfter(() => this.fromDate.value, true, `'To' date must be after 'From' date.`));
        this.fromAmount = FormInputState.new('').map(isStakeNumeric(true, 'Value is invalid.'));
        this.toAmount = FormInputState.new('')
            .map(isStakeNumeric(true, 'Value is invalid.'))
            .map(
                isStakeGreaterOrEqual(
                    () => this.fromAmount.value,
                    'A provided value must be greater than or equal to ‘Total Stake Min’.'
                )
            );
        this.types = FormInputState.new({});
        this.includeSummary = FormInputState.new(false);
        this.saveReport = FormInputState.new(false);
        this.transactionType = FormInputState.new('all');

        this.form = FormModel.group({
            account: this.account,
            fromAmount: this.fromAmount,
            toAmount: this.toAmount,
            fromDate: this.fromDate,
            toDate: this.toDate,
            types: this.types,
            includeSummary: this.includeSummary,
            saveReport: this.saveReport,
            transactionType: this.transactionType,
        });

        makeAutoObservable(this);
    }

    @computed public isChecked = (title: keyof RequestDataType['types'], option: string): boolean => {
        return this.types.value[title]?.includes(option) ?? false;
    };

    public onCheckChange = (title: keyof RequestDataType['types'], option: string): void => {
        if (this.isChecked(title, option)) {
            if (this.types.value[title] !== undefined && this.types.value[title].length === 1) {
                delete this.types.value[title];
            } else {
                this.types.setValue({
                    ...this.types.value,
                    [title]: this.types.value[title]?.filter((el) => el !== option),
                });
            }
        } else {
            this.types.setValue({
                ...this.types.value,
                [title]:
                    this.types.value[title] !== undefined && this.types.value[title].length > 0
                        ? this.types.value[title].concat([option])
                        : [option],
            });
        }
    };

    public onSubmit = async (): Promise<void> => {
        this.form.setAsVisited();
        if (this.form.result.value.type === 'ok') {
            const parsedFromAmount =
                this.form.result.value.data.fromAmount.length > 0
                    ? parseFloat(this.form.result.value.data.fromAmount)
                    : null;
            const parsedToAmount =
                this.form.result.value.data.toAmount.length > 0
                    ? parseFloat(this.form.result.value.data.toAmount)
                    : null;
            const requestTransactionType =
                this.form.result.value.data.transactionType === 'all'
                    ? ['deposit', 'withdrawal', 'bonus', 'reward']
                    : [this.form.result.value.data.transactionType];
            const aggregations: AggregationsType =
                this.form.result.value.data.includeSummary === true
                    ? { total: { type: 'count' }, totalValue: { field: 'amount', type: 'sum' } }
                    : { total: { type: 'count' } };

            const formattedPayload: RequestDataType = {
                page: 1,
                perPage: 20,
                types: omit(this.typesValue(), 'bonuses'),
                saveReport: this.form.result.value.data.saveReport,
                aggregations: aggregations,
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                enabledTransactionTypes: requestTransactionType as Array<EnabledTransactionType>,
                ...(this.form.result.value.data.account !== '' && { account: this.form.result.value.data.account }),
                createdAt: {
                    gte: this.form.result.value.data.fromDate,
                    lte: this.form.result.value.data.toDate,
                },
                amount: {
                    ...(parsedFromAmount !== null && {
                        gte: parsedFromAmount * 100,
                    }),
                    ...(parsedToAmount !== null && {
                        lte: parsedToAmount * 100,
                    }),
                },
                tagsType: this.typesValue()['bonuses'],
            };

            await this.getResults(formattedPayload);
        } else {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error occurred during form submission.',
                message: 'Please check fields are filled out correctly.',
            });
        }
    };

    public getResults = async (data: RequestDataType): Promise<void> => {
        const results = await this.trpcClient.client.paymentSearchRouter.getSearchResults.mutate(data);

        if (results.responseStatus === 'success') {
            if (results.response.aggregations?.total === 0) {
                ToasterState.get(this.common).setToast({
                    variant: 'error',
                    title: 'No search results found with these parameters.',
                    message: 'Try adjusting the filters and search again.',
                });
            }

            this.results = results.response;
            this.requestQuery = data;
        } else {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Error!',
                message: 'An error occurred retrieving payments.',
            });
        }
    };

    public clearResults = (): void => {
        this.results = undefined;
    };

    @computed public typesValue(): Record<string, string[]> {
        return toJS(this.types.value);
    }
}
